"use client";

import { getClientBuildId } from "@/lib/app-version";

const CHUNK_LOAD_MESSAGE_PATTERN =
  /(Failed to load chunk|ChunkLoadError|Loading chunk [^ ]+ failed)/i;
const CHUNK_URL_PATTERN =
  /((?:https?:\/\/[^\s)"']+)?\/_next\/static\/chunks\/[^\s)"']+\.js)/i;
const CHUNK_RESOURCE_PATH_FRAGMENT = "/_next/static/chunks/";
const MAX_RECENT_CHUNK_RESOURCES = 10;
const MAX_SCRIPT_SNIPPET_LENGTH = 240;
const SCRIPT_ERROR_RECENCY_MS = 60_000;
const VERSION_PROBE_MAX_AGE_MS = 30_000;
const VERSION_PROBE_CAPTURE_TIMEOUT_MS = 400;
const CHUNK_LOAD_RELOAD_GUARD_TTL_MS = 30 * 60 * 1000;
const CHUNK_LOAD_RELOAD_DELAY_MS = 400;
const REDACTED_PATH_SEGMENT = "[redacted]";

type PerformanceLike = Pick<Performance, "getEntriesByName" | "getEntriesByType">;
type FetchLike = typeof fetch;
type StorageLike = Pick<Storage, "getItem" | "setItem">;
type LocationLike = Pick<Location, "pathname" | "reload">;
type SetTimeoutLike = (
  handler: () => void,
  timeout: number
) => ReturnType<typeof setTimeout>;
type ClearTimeoutLike = (handle: ReturnType<typeof setTimeout>) => void;

type NetworkInformationLike = {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
};

type NavigatorLike = Pick<Navigator, "onLine" | "userAgent"> & {
  connection?: NetworkInformationLike;
};

type DocumentLike = Pick<Document, "referrer" | "visibilityState">;

export type ScriptLoadFailureSnapshot = {
  src: string;
  tagName: string;
  capturedAt: string;
  capturedAtMs: number;
  elementSnippet: string | null;
};

export type VersionProbePayload = {
  appVersion?: string;
  buildId?: string;
  release?: string;
  generatedAt?: string;
};

type VersionProbeState = {
  payload: VersionProbePayload;
  fetchedAtMs: number;
};

type VersionProbeFetchOptions = {
  abortSignal?: AbortSignal;
};

type DiagnosticsWindow = Window & {
  __NEXT_DATA__?: {
    buildId?: string;
  };
  __ASEED_CHUNK_LOAD_DIAGNOSTICS_INSTALLED__?: boolean;
  __ASEED_LAST_CHUNK_SCRIPT_ERROR__?: ScriptLoadFailureSnapshot;
  __ASEED_VERSION_PROBE_STATE__?: VersionProbeState | null;
  __ASEED_VERSION_PROBE_PROMISE__?: Promise<void> | null;
  __ASEED_CHUNK_LOAD_RECOVERY_TIMER__?: ReturnType<typeof setTimeout> | null;
};

declare global {
  interface Window {
    __ASEED_CHUNK_LOAD_DIAGNOSTICS_INSTALLED__?: boolean;
    __ASEED_LAST_CHUNK_SCRIPT_ERROR__?: ScriptLoadFailureSnapshot;
    __ASEED_VERSION_PROBE_STATE__?: VersionProbeState | null;
    __ASEED_VERSION_PROBE_PROMISE__?: Promise<void> | null;
    __ASEED_CHUNK_LOAD_RECOVERY_TIMER__?: ReturnType<typeof setTimeout> | null;
  }
}

type ChunkLoadRecoveryRecord = {
  timestamp: number;
  pathname: string;
  chunkHash: string | null;
};

type ScriptLike = {
  tagName?: unknown;
  src?: unknown;
  outerHTML?: unknown;
};

type ErrorEventLike = {
  target?: EventTarget | ScriptLike | null;
};

export type ChunkLoadErrorInfo = {
  message: string;
  chunkUrl: string | null;
  chunkHash: string | null;
  chunkFileName: string | null;
};

export type ChunkResourceSummary = {
  name: string;
  initiatorType: string;
  startTime: number;
  duration: number;
  responseEnd: number;
  transferSize?: number;
  encodedBodySize?: number;
  decodedBodySize?: number;
};

export type ChunkLoadDiagnostics = ChunkLoadErrorInfo & {
  href: string | null;
  referrer: string | null;
  online: boolean | null;
  userAgent: string | null;
  networkEffectiveType: string | null;
  networkRtt: number | null;
  networkDownlink: number | null;
  networkSaveData: boolean | null;
  visibilityState: string | null;
  clientBuildId: string | null;
  nextDataBuildId: string | null;
  versionProbe: VersionProbePayload | null;
  failedChunkResource: ChunkResourceSummary | null;
  recentChunkResources: ChunkResourceSummary[];
  lastScriptLoadFailure: ScriptLoadFailureSnapshot | null;
};

export type ChunkLoadScopeLike = {
  setTag(key: string, value: string): void;
  setContext(key: string, value: Record<string, unknown> | null): void;
};

export type ChunkLoadEventLike = {
  tags?: unknown;
  contexts?: unknown;
};

export type ChunkLoadDiagnosticsOptions = {
  targetWindow?: DiagnosticsWindow;
  document?: DocumentLike;
  navigator?: NavigatorLike;
  performanceApi?: PerformanceLike;
  fetchImpl?: FetchLike;
  versionEndpoint?: string;
  versionProbeMaxAgeMs?: number;
  now?: () => number;
};

export type PrepareChunkLoadDiagnosticsOptions = Pick<
  ChunkLoadDiagnosticsOptions,
  "fetchImpl" | "now" | "targetWindow" | "versionEndpoint" | "versionProbeMaxAgeMs"
> & {
  timeoutMs?: number;
};

export type ChunkLoadRecoveryResult = {
  isChunkLoadError: boolean;
  scheduledReload: boolean;
  blockedByGuard: boolean;
  diagnostics: ChunkLoadDiagnostics | null;
};

export type ChunkLoadRecoveryOptions = ChunkLoadDiagnosticsOptions & {
  sessionStorage?: StorageLike;
  location?: LocationLike;
  setTimeoutImpl?: SetTimeoutLike;
  clearTimeoutImpl?: ClearTimeoutLike;
  reloadGuardTtlMs?: number;
  reloadDelayMs?: number;
};

function getErrorMessage(input: unknown): string | null {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof Error) {
    return input.message;
  }

  if (
    typeof input === "object" &&
    input !== null &&
    "message" in input &&
    typeof (input as { message?: unknown }).message === "string"
  ) {
    return (input as { message: string }).message;
  }

  if (typeof input === "object" && input !== null && "reason" in input) {
    return getErrorMessage((input as { reason?: unknown }).reason);
  }

  return null;
}

function trimString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readStoredJson<T>(storage: StorageLike | undefined, key: string): T | null {
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeStoredJson(
  storage: StorageLike | undefined,
  key: string,
  value: unknown
): void {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage failures in privacy-restricted environments.
  }
}

function getSessionStorageSafe(): StorageLike | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function redactSensitivePath(pathname: string): string {
  const segments = pathname.split("/");

  const redactSegment = (index: number) => {
    if (typeof segments[index] === "string" && segments[index]) {
      segments[index] = REDACTED_PATH_SEGMENT;
    }
  };

  if (segments[1] === "interview") {
    redactSegment(2);
  }

  if (
    segments[1] === "space" &&
    segments[2] === "records" &&
    segments[3] === "public"
  ) {
    redactSegment(4);
  }

  if (
    segments[1] === "space" &&
    segments[2] === "projects" &&
    segments[3] === "public"
  ) {
    redactSegment(4);

    if (segments[5] === "reports") {
      redactSegment(6);
    }
  }

  return segments.join("/");
}

function sanitizeDiagnosticUrl(value: unknown): string | null {
  const trimmed = trimString(value);
  if (!trimmed) {
    return null;
  }

  try {
    const hasExplicitOrigin = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed);
    const parsed = new URL(trimmed, "https://diagnostics.invalid");
    const sanitizedPathname = redactSensitivePath(parsed.pathname);

    if (!hasExplicitOrigin) {
      return sanitizedPathname;
    }

    return `${parsed.origin}${sanitizedPathname}`;
  } catch {
    return trimmed;
  }
}

function extractChunkUrl(message: string): string | null {
  const matched = CHUNK_URL_PATTERN.exec(message);
  return matched?.[1] ?? null;
}

function extractChunkFileName(chunkUrl: string | null): string | null {
  if (!chunkUrl) {
    return null;
  }

  const path = chunkUrl.split("?")[0]?.split("#")[0] ?? chunkUrl;
  const segment = path.split("/").pop();
  if (!segment || !segment.endsWith(".js")) {
    return null;
  }

  return segment.slice(0, -3);
}

function isVersionProbeStateFresh(
  state: VersionProbeState | null | undefined,
  now: number,
  maxAgeMs: number
): boolean {
  if (!state) {
    return false;
  }

  return now - state.fetchedAtMs <= maxAgeMs;
}

function isChunkLoadRecoveryFresh(
  record: ChunkLoadRecoveryRecord | null,
  now: number,
  ttlMs: number
): boolean {
  if (!record) {
    return false;
  }

  return now - record.timestamp <= ttlMs;
}

function buildChunkLoadRecoveryKey(buildId: string, pathname: string): string {
  return `aseed-chunk-load-recovery:${buildId}:${pathname}`;
}

function getVersionProbePayload(
  targetWindow: DiagnosticsWindow | undefined,
  now: number,
  maxAgeMs: number
): VersionProbePayload | null {
  const state = targetWindow?.__ASEED_VERSION_PROBE_STATE__ ?? null;
  if (!isVersionProbeStateFresh(state, now, maxAgeMs)) {
    return null;
  }

  return state?.payload ?? null;
}

export function extractChunkLoadErrorInfo(input: unknown): ChunkLoadErrorInfo | null {
  const message = getErrorMessage(input);
  if (!message || !CHUNK_LOAD_MESSAGE_PATTERN.test(message)) {
    return null;
  }

  const chunkUrl = extractChunkUrl(message);
  const chunkFileName = extractChunkFileName(chunkUrl);

  return {
    message,
    chunkUrl,
    chunkHash: chunkFileName,
    chunkFileName,
  };
}

function toChunkResourceSummary(entry: PerformanceEntry): ChunkResourceSummary {
  const resourceEntry = entry as PerformanceResourceTiming;

  return {
    name: entry.name,
    initiatorType:
      typeof resourceEntry.initiatorType === "string"
        ? resourceEntry.initiatorType
        : "unknown",
    startTime: Number(entry.startTime ?? 0),
    duration: Number(entry.duration ?? 0),
    responseEnd: Number(resourceEntry.responseEnd ?? 0),
    transferSize:
      typeof resourceEntry.transferSize === "number"
        ? resourceEntry.transferSize
        : undefined,
    encodedBodySize:
      typeof resourceEntry.encodedBodySize === "number"
        ? resourceEntry.encodedBodySize
        : undefined,
    decodedBodySize:
      typeof resourceEntry.decodedBodySize === "number"
        ? resourceEntry.decodedBodySize
        : undefined,
  };
}

function listRecentChunkResources(
  performanceApi?: PerformanceLike,
  limit = MAX_RECENT_CHUNK_RESOURCES
): ChunkResourceSummary[] {
  try {
    const entries =
      performanceApi
        ?.getEntriesByType("resource")
        ?.filter((entry) => entry.name.includes(CHUNK_RESOURCE_PATH_FRAGMENT)) ??
      [];
    return entries.slice(-limit).map(toChunkResourceSummary);
  } catch {
    return [];
  }
}

function findChunkResource(
  performanceApi: PerformanceLike | undefined,
  chunkUrl: string | null
): ChunkResourceSummary | null {
  if (!chunkUrl) {
    return null;
  }

  try {
    const entry = performanceApi?.getEntriesByName(chunkUrl)?.slice(-1)[0];
    return entry ? toChunkResourceSummary(entry) : null;
  } catch {
    return null;
  }
}

function toScriptLoadFailureSnapshot(
  target: ScriptLike | EventTarget | null | undefined,
  now: number
): ScriptLoadFailureSnapshot | null {
  const scriptTarget = target as ScriptLike | null | undefined;
  const src = trimString(scriptTarget?.src);
  const tagName = trimString(scriptTarget?.tagName)?.toUpperCase() ?? null;
  if (!src || !tagName || tagName !== "SCRIPT") {
    return null;
  }

  if (!src.includes(CHUNK_RESOURCE_PATH_FRAGMENT)) {
    return null;
  }

  const snippet = trimString(scriptTarget?.outerHTML);

  return {
    src,
    tagName,
    capturedAt: new Date(now).toISOString(),
    capturedAtMs: now,
    elementSnippet: snippet ? snippet.slice(0, MAX_SCRIPT_SNIPPET_LENGTH) : null,
  };
}

export function captureChunkScriptLoadFailure(
  targetWindow: DiagnosticsWindow,
  event: ErrorEventLike,
  now = Date.now()
): ScriptLoadFailureSnapshot | null {
  const snapshot = toScriptLoadFailureSnapshot(event.target ?? null, now);
  if (!snapshot) {
    return null;
  }

  targetWindow.__ASEED_LAST_CHUNK_SCRIPT_ERROR__ = snapshot;
  return snapshot;
}

function getLastScriptLoadFailure(
  targetWindow: DiagnosticsWindow | undefined,
  now: number
): ScriptLoadFailureSnapshot | null {
  const snapshot = targetWindow?.__ASEED_LAST_CHUNK_SCRIPT_ERROR__ ?? null;
  if (!snapshot) {
    return null;
  }

  if (now - snapshot.capturedAtMs > SCRIPT_ERROR_RECENCY_MS) {
    return null;
  }

  return snapshot;
}

export function warmChunkLoadVersionProbe(
  options: Pick<
    ChunkLoadDiagnosticsOptions,
    "targetWindow" | "fetchImpl" | "versionEndpoint" | "versionProbeMaxAgeMs" | "now"
  > & {
    forceRefresh?: boolean;
  } & VersionProbeFetchOptions = {}
): Promise<void> | null {
  const targetWindow =
    options.targetWindow ??
    (typeof window !== "undefined" ? (window as DiagnosticsWindow) : undefined);
  const fetchImpl =
    options.fetchImpl ?? (typeof fetch === "function" ? fetch.bind(globalThis) : undefined);
  const versionEndpoint = options.versionEndpoint ?? "/api/version";
  const now = options.now ?? (() => Date.now());
  const currentTime = now();
  const maxAgeMs = options.versionProbeMaxAgeMs ?? VERSION_PROBE_MAX_AGE_MS;
  const currentState = targetWindow?.__ASEED_VERSION_PROBE_STATE__ ?? null;

  if (!targetWindow || !fetchImpl) {
    return null;
  }

  if (!options.forceRefresh && isVersionProbeStateFresh(currentState, currentTime, maxAgeMs)) {
    return targetWindow.__ASEED_VERSION_PROBE_PROMISE__ ?? Promise.resolve();
  }

  if (targetWindow.__ASEED_VERSION_PROBE_PROMISE__) {
    return targetWindow.__ASEED_VERSION_PROBE_PROMISE__;
  }

  const request = fetchImpl(versionEndpoint, {
    cache: "no-store",
    credentials: "same-origin",
    headers: {
      accept: "application/json",
    },
    signal: options.abortSignal,
  })
    .then(async (response) => {
      if (!response.ok) {
        return;
      }

      const payload = (await response.json()) as VersionProbePayload;
      targetWindow.__ASEED_VERSION_PROBE_STATE__ = {
        payload,
        fetchedAtMs: now(),
      };
    })
    .catch(() => {
      if (!currentState) {
        targetWindow.__ASEED_VERSION_PROBE_STATE__ = null;
      }
    })
    .finally(() => {
      if (targetWindow.__ASEED_VERSION_PROBE_PROMISE__ === request) {
        targetWindow.__ASEED_VERSION_PROBE_PROMISE__ = null;
      }
    });

  targetWindow.__ASEED_VERSION_PROBE_PROMISE__ = request;
  return request;
}

export function installChunkLoadDiagnostics(
  options: Pick<
    ChunkLoadDiagnosticsOptions,
    "targetWindow" | "fetchImpl" | "versionEndpoint"
  > = {}
): void {
  const targetWindow =
    options.targetWindow ??
    (typeof window !== "undefined" ? (window as DiagnosticsWindow) : undefined);

  if (!targetWindow || targetWindow.__ASEED_CHUNK_LOAD_DIAGNOSTICS_INSTALLED__) {
    warmChunkLoadVersionProbe(options);
    return;
  }

  targetWindow.__ASEED_CHUNK_LOAD_DIAGNOSTICS_INSTALLED__ = true;
  targetWindow.addEventListener(
    "error",
    (event) => {
      captureChunkScriptLoadFailure(targetWindow, event as ErrorEventLike);
    },
    true
  );

  warmChunkLoadVersionProbe(options);
}

export async function prepareChunkLoadDiagnosticsForCapture(
  options: PrepareChunkLoadDiagnosticsOptions = {}
): Promise<void> {
  installChunkLoadDiagnostics(options);

  const timeoutMs = options.timeoutMs ?? VERSION_PROBE_CAPTURE_TIMEOUT_MS;
  const targetWindow =
    options.targetWindow ??
    (typeof window !== "undefined" ? (window as DiagnosticsWindow) : undefined);
  const abortController =
    typeof AbortController === "function" ? new AbortController() : null;
  const refresh = warmChunkLoadVersionProbe({
    ...options,
    forceRefresh: true,
    abortSignal: abortController?.signal,
  });

  if (!refresh) {
    return;
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    await Promise.race([
      refresh.catch(() => undefined),
      new Promise<void>((resolve) => {
        timeoutId = setTimeout(() => {
          abortController?.abort();
          if (targetWindow?.__ASEED_VERSION_PROBE_PROMISE__ === refresh) {
            targetWindow.__ASEED_VERSION_PROBE_PROMISE__ = null;
          }
          resolve();
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

export function collectChunkLoadDiagnostics(
  input: unknown,
  options: ChunkLoadDiagnosticsOptions = {}
): ChunkLoadDiagnostics | null {
  const info = extractChunkLoadErrorInfo(input);
  if (!info) {
    return null;
  }

  const targetWindow =
    options.targetWindow ??
    (typeof window !== "undefined" ? (window as DiagnosticsWindow) : undefined);
  const documentRef =
    options.document ?? (typeof document !== "undefined" ? document : undefined);
  const navigatorRef =
    options.navigator ??
    (typeof navigator !== "undefined" ? (navigator as NavigatorLike) : undefined);
  const performanceApi =
    options.performanceApi ??
    (typeof performance !== "undefined" ? performance : undefined);
  const now = options.now ?? (() => Date.now());
  const currentTime = now();
  const versionProbeMaxAgeMs = options.versionProbeMaxAgeMs ?? VERSION_PROBE_MAX_AGE_MS;
  const lastScriptLoadFailure = getLastScriptLoadFailure(targetWindow, currentTime);
  const chunkUrl = info.chunkUrl;
  const chunkFileName = info.chunkFileName;
  const connection = navigatorRef?.connection;

  return {
    ...info,
    chunkUrl,
    chunkHash: chunkFileName,
    chunkFileName,
    href: sanitizeDiagnosticUrl(targetWindow?.location?.href),
    referrer: sanitizeDiagnosticUrl(documentRef?.referrer),
    online: typeof navigatorRef?.onLine === "boolean" ? navigatorRef.onLine : null,
    userAgent: trimString(navigatorRef?.userAgent) ?? null,
    networkEffectiveType: trimString(connection?.effectiveType) ?? null,
    networkRtt: typeof connection?.rtt === "number" ? connection.rtt : null,
    networkDownlink:
      typeof connection?.downlink === "number" ? connection.downlink : null,
    networkSaveData:
      typeof connection?.saveData === "boolean" ? connection.saveData : null,
    visibilityState: trimString(documentRef?.visibilityState) ?? null,
    clientBuildId: targetWindow ? getClientBuildId(targetWindow) : null,
    nextDataBuildId: trimString(targetWindow?.__NEXT_DATA__?.buildId) ?? null,
    versionProbe: getVersionProbePayload(targetWindow, currentTime, versionProbeMaxAgeMs),
    failedChunkResource: findChunkResource(performanceApi, chunkUrl),
    recentChunkResources: listRecentChunkResources(performanceApi),
    lastScriptLoadFailure,
  };
}

export function scheduleChunkLoadRecovery(
  input: unknown,
  options: ChunkLoadRecoveryOptions = {}
): ChunkLoadRecoveryResult {
  const diagnostics = collectChunkLoadDiagnostics(input, options);
  if (!diagnostics) {
    return {
      isChunkLoadError: false,
      scheduledReload: false,
      blockedByGuard: false,
      diagnostics: null,
    };
  }

  const targetWindow =
    options.targetWindow ??
    (typeof window !== "undefined" ? (window as DiagnosticsWindow) : undefined);
  const sessionStorageRef = options.sessionStorage ?? getSessionStorageSafe();
  const locationRef =
    options.location ?? (typeof window !== "undefined" ? window.location : undefined);
  const now = options.now ?? (() => Date.now());
  const setTimeoutImpl = options.setTimeoutImpl ?? setTimeout;
  const clearTimeoutImpl = options.clearTimeoutImpl ?? clearTimeout;
  const reloadGuardTtlMs = options.reloadGuardTtlMs ?? CHUNK_LOAD_RELOAD_GUARD_TTL_MS;
  const reloadDelayMs = options.reloadDelayMs ?? CHUNK_LOAD_RELOAD_DELAY_MS;

  if (!locationRef?.reload) {
    return {
      isChunkLoadError: true,
      scheduledReload: false,
      blockedByGuard: false,
      diagnostics,
    };
  }

  const buildId =
    diagnostics.clientBuildId ?? diagnostics.nextDataBuildId ?? getClientBuildId(targetWindow);
  const pathname = trimString(locationRef.pathname) ?? "/";
  const recoveryKey = buildChunkLoadRecoveryKey(buildId, pathname);
  const recoveryRecord = readStoredJson<ChunkLoadRecoveryRecord>(
    sessionStorageRef,
    recoveryKey
  );

  if (isChunkLoadRecoveryFresh(recoveryRecord, now(), reloadGuardTtlMs)) {
    return {
      isChunkLoadError: true,
      scheduledReload: false,
      blockedByGuard: true,
      diagnostics,
    };
  }

  writeStoredJson(sessionStorageRef, recoveryKey, {
    timestamp: now(),
    pathname,
    chunkHash: diagnostics.chunkHash,
  } satisfies ChunkLoadRecoveryRecord);

  if (targetWindow?.__ASEED_CHUNK_LOAD_RECOVERY_TIMER__) {
    clearTimeoutImpl(targetWindow.__ASEED_CHUNK_LOAD_RECOVERY_TIMER__);
  }

  const timer = setTimeoutImpl(() => {
    locationRef.reload();
  }, reloadDelayMs);

  if (targetWindow) {
    targetWindow.__ASEED_CHUNK_LOAD_RECOVERY_TIMER__ = timer;
  }

  return {
    isChunkLoadError: true,
    scheduledReload: true,
    blockedByGuard: false,
    diagnostics,
  };
}

function buildChunkLoadContext(diagnostics: ChunkLoadDiagnostics): Record<string, unknown> {
  return {
    message: diagnostics.message,
    chunkUrl: diagnostics.chunkUrl,
    chunkHash: diagnostics.chunkHash,
    chunkFileName: diagnostics.chunkFileName,
    href: diagnostics.href,
    referrer: diagnostics.referrer,
    online: diagnostics.online,
    userAgent: diagnostics.userAgent,
    networkEffectiveType: diagnostics.networkEffectiveType,
    networkRtt: diagnostics.networkRtt,
    networkDownlink: diagnostics.networkDownlink,
    networkSaveData: diagnostics.networkSaveData,
    visibilityState: diagnostics.visibilityState,
    clientBuildId: diagnostics.clientBuildId,
    nextDataBuildId: diagnostics.nextDataBuildId,
    versionProbe: diagnostics.versionProbe,
  };
}

function buildChunkLoadResourceTimingContext(
  diagnostics: ChunkLoadDiagnostics
): Record<string, unknown> | null {
  return diagnostics.failedChunkResource
    ? {
        failedChunkResource: diagnostics.failedChunkResource,
      }
    : null;
}

function buildChunkLoadRecentResourcesContext(
  diagnostics: ChunkLoadDiagnostics
): Record<string, unknown> {
  return {
    resources: diagnostics.recentChunkResources,
  };
}

function buildChunkLoadScriptErrorContext(
  diagnostics: ChunkLoadDiagnostics
): Record<string, unknown> | null {
  return diagnostics.lastScriptLoadFailure
    ? {
        lastScriptLoadFailure: diagnostics.lastScriptLoadFailure,
      }
    : null;
}

function toStringRecord(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object") {
    return {};
  }

  const entries = Object.entries(value).filter(
    (entry): entry is [string, string] => typeof entry[1] === "string"
  );

  return Object.fromEntries(entries);
}

function toContextRecord(
  value: unknown
): Record<string, Record<string, unknown> | undefined> {
  if (!value || typeof value !== "object") {
    return {};
  }

  const entries = Object.entries(value).filter((entry) => {
    const contextValue = entry[1];
    return contextValue === undefined || (contextValue !== null && typeof contextValue === "object");
  }) as Array<[string, Record<string, unknown> | undefined]>;

  return Object.fromEntries(entries);
}

export function applyChunkLoadDiagnosticsToScope(
  scope: ChunkLoadScopeLike,
  input: unknown,
  options: ChunkLoadDiagnosticsOptions = {}
): boolean {
  const diagnostics = collectChunkLoadDiagnostics(input, options);
  if (!diagnostics) {
    return false;
  }

  if (!diagnostics.versionProbe) {
    warmChunkLoadVersionProbe({
      targetWindow: options.targetWindow,
      fetchImpl: options.fetchImpl,
      versionEndpoint: options.versionEndpoint,
      versionProbeMaxAgeMs: options.versionProbeMaxAgeMs,
      now: options.now,
      forceRefresh: true,
    });
  }

  scope.setTag("chunk_load_failure", "true");
  if (diagnostics.chunkHash) {
    scope.setTag("chunk_hash", diagnostics.chunkHash);
  }
  if (diagnostics.clientBuildId) {
    scope.setTag("client_build_id", diagnostics.clientBuildId);
  }

  scope.setContext("chunk_load", buildChunkLoadContext(diagnostics));
  scope.setContext(
    "chunk_load_resource_timing",
    buildChunkLoadResourceTimingContext(diagnostics)
  );
  scope.setContext(
    "chunk_load_recent_resources",
    buildChunkLoadRecentResourcesContext(diagnostics)
  );
  scope.setContext("chunk_load_script_error", buildChunkLoadScriptErrorContext(diagnostics));

  return true;
}

export function applyChunkLoadDiagnosticsToEvent(
  event: ChunkLoadEventLike,
  input: unknown,
  options: ChunkLoadDiagnosticsOptions = {}
): boolean {
  const diagnostics = collectChunkLoadDiagnostics(input, options);
  if (!diagnostics) {
    return false;
  }

  const tags: Record<string, string> = {
    ...toStringRecord(event.tags),
    chunk_load_failure: "true",
  };
  if (diagnostics.chunkHash) {
    tags.chunk_hash = diagnostics.chunkHash;
  }
  if (diagnostics.clientBuildId) {
    tags.client_build_id = diagnostics.clientBuildId;
  }

  const contexts: Record<string, Record<string, unknown> | undefined> = {
    ...toContextRecord(event.contexts),
    chunk_load: buildChunkLoadContext(diagnostics),
    chunk_load_recent_resources: buildChunkLoadRecentResourcesContext(diagnostics),
  };

  const chunkLoadResourceTiming = buildChunkLoadResourceTimingContext(diagnostics);
  if (chunkLoadResourceTiming) {
    contexts.chunk_load_resource_timing = chunkLoadResourceTiming;
  } else {
    delete contexts.chunk_load_resource_timing;
  }

  const chunkLoadScriptError = buildChunkLoadScriptErrorContext(diagnostics);
  if (chunkLoadScriptError) {
    contexts.chunk_load_script_error = chunkLoadScriptError;
  } else {
    delete contexts.chunk_load_script_error;
  }

  event.tags = tags;
  event.contexts = contexts;

  return true;
}
